<template>
  <v-card variant="text">
    <v-card-text>
      <v-container fluid>
        <v-row v-if="props.field.properties.showDescription">
          <v-col>
            <v-container fluid>
              <v-row>
                <v-col class="px-0">
                  <v-card-title class="px-0">Description</v-card-title>
                </v-col>
              </v-row>
              <v-row
                v-for="(desc, index) in model.description"
                :key="index"
                align="center"
              >
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="desc.key"
                    density="compact"
                    label="Key"
                    color="primary"
                    placeholder="Key"
                    hide-details="auto"
                    @blur="refreshEditorRaw"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="desc.value"
                    density="compact"
                    label="Value"
                    color="primary"
                    placeholder="Value"
                    hide-details="auto"
                    @blur="refreshEditorRaw"
                  />
                </v-col>
                <v-col cols="auto" md="4">
                  <v-btn
                    color="primary"
                    prepend-icon="mdi-minus"
                    hide-details="auto"
                    @click="removeDescriptionField(index)"
                    >Remove Description</v-btn
                  >
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn
                    color="primary"
                    prepend-icon="mdi-plus"
                    @click="addDescriptionField"
                  >
                    Add Description
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pa-0 d-flex align-center">
            <v-card-title class="my-4">
              {{ props.field?.properties.title ?? 'Config Editor Layout' }}
            </v-card-title>
            <EquipmentEditorHelp />
          </v-col>
        </v-row>
        <v-row v-if="settingEditorErrors.length">
          <v-col cols="12">
            <v-alert
              v-for="(errorMessage, index) in settingEditorErrors"
              :key="index"
              class="validation-message"
              type="error"
            >
              {{ errorMessage }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-switch
              v-model="layoutSideBySide"
              type="success"
              color="success"
              label="Seitenansicht"
              hide-details="auto"
            /> </v-col
        ></v-row>
        <v-row v-if="!layoutSideBySide">
          <v-col cols="12">
            <v-tabs v-model="currentTabValue" bg-color="primary">
              <v-tab text="Code" :value="0" prepend-icon="mdi-code-tags">
              </v-tab>
              <v-tab text="Preview" :value="1" prepend-icon="mdi-file-image">
              </v-tab>
            </v-tabs>
            <v-tabs-window v-model="currentTabValue">
              <v-tabs-window-item :value="0">
                <codemirror
                  v-model="settingEditorRaw"
                  :options="editorOptions"
                  @blur="settingEditorRawChanged()"
              /></v-tabs-window-item>
              <v-tabs-window-item :value="1">
                <FormBuilder
                  ref="configBuilder"
                  :form="formBuilderForm"
                  :config="editorConfig"
                  @error="handleError"
                />
              </v-tabs-window-item>
            </v-tabs-window>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" md="6">
            <v-card-title class="my-4">Code</v-card-title>
            <codemirror
              v-model="settingEditorRaw"
              :options="editorOptions"
              @update:model-value="debouncedSettingEditorRawChanged()"
              @blur="settingEditorRawChanged()"
            />
          </v-col>
          <v-col>
            <v-card-title class="my-4">Preview</v-card-title>
            <v-sheet>
              <FormBuilder
                ref="formBuilderRef"
                :form="formBuilderForm"
                :config="editorConfig"
                @error="handleError"
              />
            </v-sheet>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-expansion-panels color="primary">
              <v-expansion-panel title="Daten Output">
                <v-expansion-panel-text>
                  <codemirror v-model="configOutput" language="json" />
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts" generic="T">
import parseJson from 'json-parse-better-errors'
import debounce from 'lodash.debounce'
import { computed, onMounted, ref } from 'vue'
import { Codemirror } from 'vue-codemirror'

import EquipmentEditorHelp from '@/apps/brelag/brelag-admin/components/equipment/EquipmentEditorHelp.vue'
import FormBuilder from '@/components/common/forms/FormBuilder.vue'
import { defaultEditorOptions } from '@/util/CodeMirror'

import type { SchemaFormKitProps } from './formkit/types'
const props = defineProps<SchemaFormKitProps>()

const currentTabValue = ref(0)
const model = defineModel<{
  description: Array<{
    key: string
    value: string
  }>
}>()
const settingEditorErrors = ref([])
const editorOptions = ref(defaultEditorOptions)
const layoutSideBySide = ref(true)
const settingEditorRaw = ref('')
const editorConfig = ref({ model: {}, fields: [], tabs: [] })
const formBuilderForm = ref({})
const configBuilder = ref(null)
const formBuilderRef = ref(null)
const configBuilderReady = ref(false)
const debouncedSettingEditorRawChanged = debounce(settingEditorRawChanged, 100)

onMounted(() => {
  refreshEditorRaw()
  configBuilderReady.value = true
})

function refreshEditorRaw() {
  if (!model.value) {
    settingEditorRaw.value = JSON.stringify(
      {
        title: 'Editor',
        model: {},
        tabs: [],
      },
      null,
      2
    )
  } else {
    settingEditorRaw.value = JSON.stringify(model.value, null, 2)
  }
  settingEditorRawChanged()
}

function removeDescriptionField(index) {
  model.value.description.splice(index, 1)
  refreshEditorRaw()
}

function addDescriptionField() {
  if (model.value.description) {
    model.value.description.push({
      key: '',
      value: '',
    })
  } else {
    model.value.description = [{ key: '', value: '' }]
  }
  refreshEditorRaw()
}

const configOutput = computed(() => {
  if (configBuilderReady.value && configBuilder.value !== null) {
    return JSON.stringify(configBuilder.value.model, null, 2)
  } else if (configBuilderReady.value && formBuilderRef.value !== null) {
    return JSON.stringify(formBuilderRef.value.model, null, 2)
  } else {
    return ''
  }
})

function clearErrors() {
  settingEditorErrors.value = []
}

function settingEditorRawChanged() {
  clearErrors()
  try {
    const newLayout = parseJson(settingEditorRaw.value)
    editorConfig.value = newLayout
    settingEditorErrors.value = []
    model.value = editorConfig.value
  } catch (e) {
    settingEditorErrors.value.push(e.toString())
  }
}

function handleError(exception) {
  settingEditorErrors.value.push(exception.toString())
}
</script>

<style lang="scss" scoped>
.token.number {
  font-size: 0.75rem;
}

.cm-s-base16-light .CodeMirror-cursor {
  border-left: 2px solid #505050;
}

:deep(.cm-scroller) {
  overflow: 'auto';
}

:deep(.cm-editor) {
  max-height: 400px;
  background-color: #f5f5f5;
}
</style>
